import React from 'react';

const About: React.FC = () => {
  return (
    <div className="App">
      {/* Hero Section */}
      <section className="hero about-hero-section">
        <div className="container">
          <div className="hero-content about-hero animate-fade-in-up">
            <h1 className="hero-title">About Me</h1>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section about-page-section">
        <div className="container">
          <div className="grid about-grid">
            {/* Portrait image first so it appears on the left */}
            <div className="animate-fade-in-up">
              <img 
                src="/img/about-potrait.jpg" 
                alt="Professional portrait of Sanchita Kamath, a young researcher with dark hair and wheatish skin, wearing professional attire." 
                className="w-full rounded-lg shadow-md about-image"
              />
            </div>
            {/* Text block second so it appears on the right */}
            <div className="animate-fade-in-up">
              <div className="card-description mb-4">
                <p className="mb-3">
                  Sanchita Kamath is a Ph.D. candidate in Information Sciences at the University of Illinois Urbana-Champaign, researching in the (x)Ability Design Lab under Dr. JooYoung Seo. Her work focuses on AI-assisted human-human and human-AI collaboration: designing and evaluating multimodal, explainable interfaces, conversational agents, and accessible data systems that help people make trustworthy decisions with AI and with each other, particularly in inclusive and accessibility-focused contexts. <br/><br/>
                  She builds end-to-end systems using WebGL, D3.js, React, and Node.js, paired with mixed-methods evaluation, controlled experiments, surveys, statistical modeling, and qualitative analysis. Her work has been published at ACM CHI, ACM ASSETS, IEEE, and in journals including <em>Disability and Rehabilitation: Assistive Technology</em> and <em>mHealth</em>, and she has taught courses in ethics, database design, and computer science.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Interests */}
      <section className="section bg-secondary">
        <div className="container">
          <h2 className="section-title text-center">Research Interests</h2>
          <div className="grid grid-3 animate-stagger">
            <div className="card">
              <h3 className="card-title">Embodied Cognition</h3>
              <p className="card-description">
                Investigating how physical embodiment and sensorimotor experiences shape cognitive processes and 
                interaction with digital interfaces, particularly in virtual and augmented reality environments.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">AI-Assisted Collaboration</h3>
              <p className="card-description">
                Studying how AI can mediate and strengthen both human-AI and human-human collaboration, from
                conversational agents to multimodal interfaces that support trust, shared understanding, and
                joint decision-making.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Accessible Data Visualization</h3>
              <p className="card-description">
                Developing novel approaches to make data visualization accessible to users with visual impairments 
                through multimodal feedback, sonification, and tactile representation techniques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="section">
        <div className="container">
          <h2 className="section-title text-center">Core Competencies</h2>
          <div className="grid grid-3 animate-stagger">
            <div className="card">
              <h3 className="card-title">Research Strategy</h3>
              <ul className="card-description competency-list">
                <li>• Designing comprehensive research plans aligned with business objectives</li>
                <li>• Mapping research questions to appropriate methodologies</li>
                <li>• Reducing research debt through systemic knowledge management</li>
                <li>• Forecasting user behavior trends using mixed-method approaches</li>
                <li>• Balancing immediate insights with long-term research goals</li>
              </ul>
            </div>
            <div className="card">
              <h3 className="card-title">Methodological Expertise</h3>
              <ul className="card-description competency-list">
                <li>• Moderated user interviews & usability testing</li>
                <li>• Longitudinal studies & diary research</li>
                <li>• Advanced survey design with statistical validation</li>
                <li>• A/B testing design & implementation</li>
                <li>• Multi-modal accessibility research</li>
                <li>• Quantitative analysis (Python, R)</li>
                <li>• Journey mapping & experience modeling</li>
              </ul>
            </div>
            <div className="card">
              <h3 className="card-title">Research Impact</h3>
              <ul className="card-description competency-list">
                <li>• Stakeholder alignment & expectation management</li>
                <li>• Executive-ready research presentations</li>
                <li>• Cross-functional team collaboration</li>
                <li>• Research democratization & team workshops</li>
                <li>• Translation of findings into design requirements</li>
                <li>• Measurable ROI tracking for research initiatives</li>
                <li>• International research coordination</li>
              </ul>
            </div>
          </div>
        </div>
      </section>



      {/* Education */}
      <section className="section">
        <div className="container">
          <h2 className="section-title text-center">Education</h2>
          <div className="grid grid-1 animate-stagger">
            <div className="card">
              <h3 className="card-title">Ph.D. in Information Sciences</h3>
              <p className="body-small text-secondary mb-2">University of Illinois at Urbana-Champaign | 2023-2028 (expected)</p>
              <p className="card-description">
                Specializing in Human-Computer Interaction with a focus on cognitive modeling and AI-enhanced user interactions. 
                My dissertation will explore how sense-making strategies can improve AI interpretability and usability.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Master of Science in Information Sciences</h3>
              <p className="body-small text-secondary mb-2">University of Illinois at Urbana-Champaign | 2023-2024</p>
              <p className="card-description">
                Coursework in advanced research methods, HCI theory, and information architecture.
              </p>
            </div>
            <div className="card">
              <h3 className="card-title">Bachelor of Technology in Computer Science</h3>
              <p className="body-small text-secondary mb-2">Manipal Academy of Higher Education - Dubai Campus | 2019-2023</p>
              <p className="card-description">
                Minor in Data Analytics. Thesis research examined privacy concerns of older adults when interacting with voice agents 
                in smart home environments, combining ethnographic methods with quantitative privacy measurement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title text-center">Let's Connect</h2>
          <div className="text-center">
            <p className="body-large mb-4">
              I'm always interested in discussing research opportunities, collaboration, and innovative approaches to inclusive design.
            </p>
            <div className="flex justify-center gap-4">
              <a href="mailto:ssk11@illinois.edu" className="btn btn-dark">
                Email Me
              </a>
              <a href="https://www.linkedin.com/in/sanchitakamath/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                LinkedIn
              </a>
              <a href="https://github.com/sk-143381" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                GitHub
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
